import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';

import { CollectMissionsDialog } from '#src/components/missions/CollectMissionsDialog/index.tsx';
import { SetCollectionTask } from '#src/components/missions/SetCollectionTask/index.tsx';

import { TaskClaimedDialogs } from '../TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '../TaskClaimedDialogs/useTaskClaimedDialogs.tsx';
import type { FeaturedCollectionTask_taskInterface } from './__generated__/index.graphql.ts';

const Root = styled(Bezel)`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: var(--triple-unit) var(--double-unit);
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
`;

const TextureBackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  & > * {
    width: 100%;
    height: 100%;
  }
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(var(--c-rgb-black), 0.1) 0%,
      rgba(var(--c-rgb-black), 0.85) 100%
    );
  }
`;

type Props = {
  task?: FeaturedCollectionTask_taskInterface | null;
};

export const FeaturedCollectionTask = ({ task }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  if (!task) {
    return null;
  }

  return (
    <>
      <Root borderColor="var(--c-set-surface-primary)">
        <TextureBackgroundWrapper>
          <TextureBackground
            background="missions"
            mode="light"
            color="var(--c-set-surface-primary)"
            withDots
          />
        </TextureBackgroundWrapper>
        <SetCollectionTask
          task={task}
          onClaim={async () => onClaim(task)}
          loading={loadingTaskId === task.id}
          onClick={null}
        />
        <Button
          size="medium"
          color="quaternary"
          onClick={() => setDialogOpen(true)}
        >
          <FormattedMessage defaultMessage="View all Collect Missions" />
        </Button>
      </Root>
      <CollectMissionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
        }}
      />
    </>
  );
};

FeaturedCollectionTask.fragments = {
  taskInterface: gql`
    fragment FeaturedCollectionTask_taskInterface on TaskInterface {
      id
      ...SetCollectionTask_taskInterface
      ...TaskClaimedDialogs_taskInterface
    }
    ${SetCollectionTask.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<FeaturedCollectionTask_taskInterface>,
};
